import { createStyles, makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { getMediaContent, PointManipulationInterface } from '../../../../api/chatApi/chatApi'
import ImageMapper from 'react-image-mapper'
import useImageSize from '../../../../hooks/useImageSize'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    },
    imageContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
      minHeight: '700px',
      [theme.breakpoints.down('sm')]: {
        minHeight: '400px',
      }
    },
    imageMapperDiv: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      position: 'relative',
      paddingTop: '15px',
      '& img': {
        maxWidth: '100%',
        maxHeight: '700px',
        objectFit: 'contain',
        width: '100%',
        height: 'auto',
      },
      [theme.breakpoints.down('sm')]: {
        paddingTop: '5px',
      }
    },
    mapContainer: {
      position: 'relative',
      width: '100%',
      height: '100%',
    },
    legend: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
      backgroundColor: '#d1e6fc',
      padding: '10px',
      borderRadius: '10px',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(1),
      }
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        justifyContent: 'center',
      }
    },
    legendDot: {
      width: 12,
      height: 12,
      borderRadius: '50%'
    },
    legendText: {
      fontSize: '0.875rem'
    },
    dialogCorrect: {
      border: '2px solid green',
      borderRadius: '8px',
    },
    dialogIncorrect: {
      border: '2px solid red',
      borderRadius: '8px',
    },
  })
)

type Props = {
  image: string
  dots?: Array<PointManipulationInterface & { 
    selectedInjection?: string;
    isCorrect?: boolean;
  }>
  onSelectDot?: (dot: PointManipulationInterface) => void
  selectedBodyPartId: number | null | undefined
  selectedSideId: number | null | undefined
  selectedMethodId?: number | null | undefined
  onConstructor?: boolean
}

export default React.memo(function ImageWithDotsManipulation(props: Props) {
  const classes = useStyles({ image: props.image });
  const [imageWidth] = useImageSize(props.image);
  const [img, setImg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dots, setDots] = useState<Array<PointManipulationInterface & { 
    selectedInjection?: string;
    isCorrect?: boolean;
  }>>([]);
  const [selectedDot, setSelectedDot] = useState<PointManipulationInterface | null>(null);
  const [showInjectionDialog, setShowInjectionDialog] = useState(false);
  const lastImageRef = useRef('');
  const [imageId, setImageId] = useState('');
  const processedDotsRef = useRef<string>('');

  useEffect(() => {
    const dotsKey = JSON.stringify(props.dots || []);
    if (processedDotsRef.current === dotsKey) {
      return;
    }
    
    processedDotsRef.current = dotsKey;
    setDots([]); 
    if (Array.isArray(props.dots)) {
      setDots(props.dots.filter(dot => 
        dot && 
        typeof dot.point_x === 'number' && 
        typeof dot.point_y === 'number' &&
        dot.point_x >= 0 && 
        dot.point_y >= 0
      ));
    }
  }, [props.dots, props.selectedBodyPartId, props.selectedSideId, props.selectedMethodId]);

  const res = useCallback(async (data: any) => {
    if (lastImageRef.current === data) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      let imageBase64 = '';
      
      if (typeof data === 'string') {
        if (data.startsWith('data:image')) {
          imageBase64 = data.split(',')[1];
        } 
        else if (/^[A-Za-z0-9+/=]+$/.test(data)) {
          imageBase64 = data;
        } 
        else {
          const response = await getMediaContent(data);
          imageBase64 = response.data.data_base64 || '';
        }
      } else if (Array.isArray(data) && data.length > 0) {
        const response = await getMediaContent(data[0]);
        imageBase64 = response.data.data_base64 || '';
      } else if (data && typeof data === 'object') {
        if ('path' in data && typeof data.path === 'string') {
          const response = await getMediaContent(data.path);
          imageBase64 = response.data.data_base64 || '';
        } else {
          console.error('Invalid object data for getMediaContent:', data);
          setIsLoading(false);
          return;
        }
      } else {
        console.error('Invalid image data:', data);
        setIsLoading(false);
        return;
      }
      
      lastImageRef.current = data;
      const newImageId = Date.now().toString();
      setImageId(newImageId);
      setImg(imageBase64);
    } catch (error) {
      console.error('Error loading image:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if(props.image) {
      res(props.image);
    }
  }, [props.image, res]);

  const dotsMap = useMemo(() => {
    
    return {
      name: 'my-map',
      areas: (dots || []).map((dot) => {
        const stableArea = {
          id: dot.id?.toString() || `dot-${Math.random()}`,
          shape: 'circle',
          coords: [dot.point_x, dot.point_y, 10],
          preFillColor: dot.selectedInjection 
            ? (dot.isCorrect ? 'rgba(0, 255, 0, 0.5)' : 'rgba(255, 0, 0, 0.5)')
            : 'grey',
          strokeColor: dot.selectedInjection
            ? (dot.isCorrect ? 'green' : 'red')
            : 'grey',
          itemId: dot.id,
          pointX: dot.point_x,
          pointY: dot.point_y,
          hasInjection: !!dot.selectedInjection
        };
        return stableArea;
      }),
    };
  }, [dots]);

  const onClickPoint = useCallback((area: any) => {
    if (!props.onSelectDot) {
      return;
    }
    
    const dot = dots.find(d => d.id === area.itemId);
    
    if (!dot) {
      return;
    }
    
    if (dot.selectedInjection) {
      setSelectedDot(dot);
      setShowInjectionDialog(true);
    } else {
      props.onSelectDot(dot);
    }
  }, [props.onSelectDot, dots]);

  const onTouchStartHandler = useCallback(() => {}, []);
  const onTouchEndHandler = useCallback(() => {}, []);
  const onMouseEnter = useCallback(() => {
    const dot: HTMLMapElement | null = document.querySelector('map')
    if (dot) dot.style.cursor = 'default'
  }, []);

  const MapperComponent = useMemo(() => {
    if (!img || isLoading) {
      return null;
    }
    
    const mapperKey = `mapper-${imageId}`;
    
    return (
      <div style={{ position: 'relative' }}>
        <ImageMapper
          key={mapperKey}
          src={`data:image/jpeg;base64,${img}`}
          width={230}
          responsive={true}
          parentWidth={230}
          imgWidth={imageWidth}
          map={dotsMap}
          onTouchStart={onTouchStartHandler}
          onTouchEnd={onTouchEndHandler}
          onClick={onClickPoint}
          stayHighlighted={false}
          onMouseEnter={onMouseEnter}
        />
      </div>
    );
  }, [img, isLoading, imageWidth, dotsMap, onTouchStartHandler, onTouchEndHandler, onClickPoint, onMouseEnter, imageId]);


  const selectedDotsCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const dotsWithInjections = dots.filter(d => d.selectedInjection);
    if (!selectedDotsCanvasRef.current || dotsWithInjections.length === 0) {
      return;
    }

    if (!imageRef.current) {
      imageRef.current = document.querySelector('.img-mapper-img');
    }
    
    const img = imageRef.current;
    if (!img) {
      return;
    }

    const canvas = selectedDotsCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    canvas.width = img.clientWidth;
    canvas.height = img.clientHeight;

    const scale = canvas.width / (imageWidth || 1);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    dotsWithInjections.forEach(dot => {
      if (!dot.selectedInjection) {
        return;
      }
      
      if (typeof dot.point_x !== 'number' || typeof dot.point_y !== 'number') {
        console.warn('Неверные координаты точки:', dot);
        return;
      }
      
      const x = dot.point_x * scale;
      const y = dot.point_y * scale;
      const radius = 10 * scale;


      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI);
      ctx.fillStyle = dot.isCorrect ? '#9ac595' : '#FF665E';
      ctx.strokeStyle = dot.isCorrect ? '#9ac595' : '#FF665E';
      ctx.lineWidth = 2;
      ctx.fill();
      ctx.stroke();
    });
  }, [dots, imageWidth]);

  return (
    <div className={classes.root}>
      <div className={classes.imageContainer}>
        <div className={classes.imageMapperDiv}>
          {MapperComponent}
          <canvas 
            ref={selectedDotsCanvasRef}
            style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 10
            }}
          />
        </div>
        <map name='newCoords' className='mapContainer' id='newCoords' />
      </div>
      
      {!props.onConstructor && (
        <div className={classes.legend}>
          <div className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: 'grey' }} />
            <span className={classes.legendText}>Не выбран</span>
          </div>
          <div className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: 'green' }} />
            <span className={classes.legendText}>Правильно</span>
          </div>
          <div className={classes.legendItem}>
            <div className={classes.legendDot} style={{ backgroundColor: 'red' }} />
            <span className={classes.legendText}>Неправильно</span>
          </div>
        </div>
      )}

      <Dialog
        open={showInjectionDialog}
        onClose={() => setShowInjectionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: selectedDot?.isCorrect ? classes.dialogCorrect : classes.dialogIncorrect
        }}
      >
        <DialogTitle>Выбранная инъекция</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: '16px' }}>
            {selectedDot?.selectedInjection || 'Инъекция не выбрана'}
          </div>
          <div style={{ 
            color: selectedDot?.isCorrect ? 'green' : 'red',
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}>
            {selectedDot?.isCorrect ? '✓ Правильный ответ' : '✗ Неправильный ответ'}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInjectionDialog(false)} color="primary">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
});
